import { httpClient } from "@/shared/http/httpClient";
import type {
    Vb360AccountDto, Vb360ContextDto, Vb360Coverage, Vb360Discrepancy, Vb360Evidence,
    Vb360Failure, Vb360PersonDto, Vb360ResolveDto,
} from "@/features/vb360/types/vb360.types";

const BASE = "/api/v1/vb360";

export class Vb360ApiError extends Error {
    readonly failure: Vb360Failure;

    constructor(failure: Vb360Failure) {
        super(`VB360 request failed (${failure.kind})`);
        this.name = "Vb360ApiError";
        this.failure = failure;
    }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && Array.isArray(value) === false;

const isArrayOf = <T>(value: unknown, guard: (item: unknown) => item is T): value is T[] =>
    Array.isArray(value) && value.every((item: unknown) => guard(item));

const isText = (value: unknown): value is string => typeof value === "string";

const isOptionalText = (value: unknown): value is string | undefined => value === undefined || typeof value === "string";

const isOptionalNumber = (value: unknown): value is number | undefined => value === undefined || typeof value === "number";

const isFailure = (value: unknown): value is Vb360Failure =>
    isRecord(value) && (value.kind === "forbidden" || value.kind === "not_found" || value.kind === "unavailable"
        || value.kind === "rate_limited" || value.kind === "invalid");

const failureOf = (error: unknown): Vb360Failure => {
    if (isRecord(error) && isFailure(error.failure)) return error.failure;
    return { kind: "unavailable" };
};

const statusToFailure = (status: number): Vb360Failure => {
    if (status === 403) return { kind: "forbidden" };
    if (status === 404) return { kind: "not_found" };
    if (status === 429) return { kind: "rate_limited" };
    if (status === 400) return { kind: "invalid" };
    return { kind: "unavailable" };
};

const isWorkspace = (value: unknown): value is { id: string; label: string } =>
    isRecord(value) && isText(value.id) && isText(value.label);

const isContext = (value: unknown): value is Vb360ContextDto =>
    isRecord(value) && value.canRead === true && isArrayOf(value.workspaces, isWorkspace);

const isResolve = (value: unknown): value is Vb360ResolveDto => {
    if (isRecord(value) === false) return false;
    if (value.status === "linked") return isText(value.entityId);
    return value.status === "unresolved";
};

const isEvidence = (value: unknown): value is Vb360Evidence =>
    isRecord(value)
    && (value.kind === "account_reference" || value.kind === "backlink")
    && (value.source === "accounts-read" || value.source === "entities-read")
    && isOptionalNumber(value.observedAt) && isOptionalNumber(value.schemaVersion);

const isAccount = (value: unknown): value is Vb360AccountDto =>
    isRecord(value) && isText(value.workspaceId) && isText(value.workspaceLabel) && isText(value.accountId)
    && isOptionalText(value.displayName) && isArrayOf(value.evidence, isEvidence);

const isRef = (value: unknown): value is { workspaceId: string; accountId: string } =>
    isRecord(value) && isText(value.workspaceId) && isText(value.accountId);

const isIsolatedBacklink = (value: unknown): value is { workspaceId: string; workspaceLabel: string; accountId: string } =>
    isRecord(value) && isText(value.workspaceId) && isText(value.workspaceLabel) && isText(value.accountId);

const isDiscrepancy = (value: unknown): value is Vb360Discrepancy =>
    isRecord(value)
    && (value.code === "backlink_target_points_elsewhere" || value.code === "account_reference_without_backlink")
    && isArrayOf(value.refs, isRef);

const isCoverage = (value: unknown): value is Vb360Coverage =>
    isRecord(value)
    && (value.accountsEnumeration === "complete" || value.accountsEnumeration === "paged")
    && (value.backlinksChecked === "complete" || value.backlinksChecked === "partial" || value.backlinksChecked === "not_checked")
    && isRecord(value.sourceObservedAt)
    && isOptionalNumber(value.sourceObservedAt.accounts) && isOptionalNumber(value.sourceObservedAt.entity);

const isPagination = (value: unknown): value is { nextCursor?: string } =>
    isRecord(value) && isOptionalText(value.nextCursor);

const isEntity = (value: unknown): value is { id: string; type: string; displayName?: string } =>
    isRecord(value) && isText(value.id) && isText(value.type) && isOptionalText(value.displayName);

const hasPersonBase = (value: Record<string, unknown>): boolean =>
    isArrayOf(value.accounts, isAccount) && isArrayOf(value.isolatedBacklinks, isIsolatedBacklink)
    && isArrayOf(value.discrepancies, isDiscrepancy) && isCoverage(value.coverage) && isPagination(value.pagination);

const isPerson = (value: unknown): value is Vb360PersonDto => {
    if (isRecord(value) === false || hasPersonBase(value) === false) return false;
    if (value.resolution === "complete") return isEntity(value.entity);
    return value.resolution === "incomplete" && value.entity === null && isText(value.referencedEntityId);
};

const requestJson = async (path: string, init?: RequestInit): Promise<unknown> => {
    const response = await httpClient.fetchWithAuth(path, init);
    if (response.ok === false) throw new Vb360ApiError(statusToFailure(response.status));
    try {
        return await response.json();
    } catch {
        throw new Vb360ApiError({ kind: "unavailable" });
    }
};

const parsed = <T>(value: unknown, guard: (candidate: unknown) => candidate is T): T => {
    if (guard(value)) return value;
    throw new Vb360ApiError({ kind: "unavailable" });
};

const getContext = async (): Promise<Vb360ContextDto> => parsed(await requestJson(`${BASE}/context`), isContext);

const resolvePerson = async (input: { workspaceId: string; accountId: string }): Promise<Vb360ResolveDto> => parsed(
    await requestJson(`${BASE}/persons/resolve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workspaceId: input.workspaceId, accountId: input.accountId }),
    }),
    isResolve,
);

const getPerson = async (entityId: string, cursor?: string): Promise<Vb360PersonDto> => parsed(
    await requestJson(`${BASE}/persons/${encodeURIComponent(entityId)}${cursor === undefined ? "" : `?cursor=${encodeURIComponent(cursor)}`}`),
    isPerson,
);

export const vb360Api = { getContext, resolvePerson, getPerson, failureOf };
